import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

// Route changes arrive as a soft bloom of light that swells from the point the
// visitor touched (or the viewport's heart for keyboard/back navigation), then
// recedes as the next room settles in.
const FRESH_MS = 1500;

export default function RouteBloom() {
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const first = useRef(true);
  const origin = useRef({ x: 50, y: 42, at: -Infinity });
  const [bloom, setBloom] = useState(null);

  useEffect(() => {
    const onDown = (e) => {
      origin.current = {
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
        at: performance.now(),
      };
    };
    window.addEventListener("pointerdown", onDown, { passive: true, capture: true });
    return () => window.removeEventListener("pointerdown", onDown, { capture: true });
  }, []);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const o = origin.current;
    const fresh = performance.now() - o.at < FRESH_MS;
    setBloom({ key: performance.now(), x: fresh ? o.x : 50, y: fresh ? o.y : 42 });
  }, [pathname]);

  if (reduce || !bloom) return null;

  return (
    <motion.div
      key={bloom.key}
      data-testid="route-bloom"
      aria-hidden="true"
      className="route-bloom pointer-events-none fixed inset-0 z-[55]"
      style={{
        "--bloom-x": `${bloom.x}%`,
        "--bloom-y": `${bloom.y}%`,
        transformOrigin: `${bloom.x}% ${bloom.y}%`,
      }}
      initial={{ opacity: 0, scale: 0.55 }}
      animate={{ opacity: [0, 0.9, 0], scale: [0.55, 1.25, 2.1] }}
      transition={{ duration: 1.1, times: [0, 0.36, 1], ease: ["easeOut", "easeInOut"] }}
      onAnimationComplete={() => setBloom(null)}
    />
  );
}
